import React, { useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";

import Span from "components/typography/Span";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";
import { useToast } from "components/toast";
import { getProviderAlternatives, updateQuoteRequest } from "api/QuoteRequests";

/**
 * SHARED provider + price controls for a priced estimate service.
 *
 * WHY THIS IS ITS OWN FILE (RC5, Session 55): these controls were a
 * `const` local to `QuoteRequests/components/Items.tsx`, which meant the
 * only way to change a provider or override a price was to be standing
 * on the RFQ page. The Work Order case file could therefore show the
 * price but not touch it, and sent the operator away with an "Adjust
 * pricing on RFQ" link. Extracting the component lets the SAME controls
 * mount on the WO surface: the logic still lives once (RC4), the
 * operator stops leaving the record (RC5). See
 * docs/maintenance-module/design/WORK_IN_PLACE_AUDIT.md and mockup 27.
 *
 * Behavior is unchanged from the version that shipped inside Items.tsx
 * (Session 50, mockup 24 Option A) — this was an extraction, not a
 * rewrite. Only the hard-coded status hexes moved onto the maintenance
 * tokens.
 *
 * CONTRACT FOR CALLERS: every write here mints a NEW estimate version on
 * the RFQ and re-selects services. `onChanged` MUST refetch the RFQ, or
 * the caller will render a stale price against a new version.
 */
export default function ProviderPriceControls({
  rfq,
  service,
  nteAmount,
  onChanged,
}: {
  rfq: any;
  service: any;
  nteAmount: number | null;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [loadingAlts, setLoadingAlts] = useState(false);
  const [alternatives, setAlternatives] = useState<any[]>([]);
  const [unprovisioned, setUnprovisioned] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState<string>(String(service?.price ?? ""));

  const money = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD" });

  // Preserve current selection across the versioned update — the RFQ
  // update rebuilds estimate services, and selectServices drives which
  // rows stay selected on the new version.
  const selectedIds = (rfq?.estimate?.services ?? [])
    .filter((s: any) => s.selected)
    .map((s: any) => s.id);

  const loadAlternatives = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoadingAlts(true);
    const res = await getProviderAlternatives({ serviceId: service.id });
    setAlternatives(Array.isArray(res?.alternatives) ? res.alternatives : []);
    setUnprovisioned(Array.isArray(res?.unprovisioned) ? res.unprovisioned : []);
    setLoadingAlts(false);
  };

  // Swap provider: send this service with the chosen depotId. The RFQ
  // update re-resolves depot/service/product and re-prices from that
  // depot's combo, minting a new estimate version.
  const swapProvider = async (alt: any) => {
    if (alt.isCurrent) return;
    setSaving(true);
    const qty = Number(service.quantity);
    const services = [
      {
        id: service.id,
        depotId: alt.depotId,
        serviceId: service.serviceId ?? service.service?.id,
        productId: service.product?.id,
        ...(qty >= 1 ? { quantity: qty } : {}),
      },
    ];
    const res = await updateQuoteRequest({
      data: { id: rfq.id },
      update: { services, selectServices: selectedIds },
    });
    setSaving(false);
    if (res?.id) {
      toast.add("Provider updated. Pricing repriced from the new depot.", "var(--theme-ui-colors-success)");
      setOpen(false);
      onChanged();
    } else {
      toast.add(res?.message || "Could not change provider.", "var(--theme-ui-colors-danger)");
    }
  };

  // Override price/unit: keep the depot, send an explicit price. The RFQ
  // update honors the explicit price on the new estimate version.
  const saveOverride = async () => {
    const parsed = parseFloat(priceInput);
    if (isNaN(parsed) || parsed < 0) {
      toast.add("Enter a valid price.", "var(--theme-ui-colors-danger)");
      return;
    }
    setSaving(true);
    const qty = Number(service.quantity);
    const services = [
      {
        id: service.id,
        depotId: service.depot?.id,
        serviceId: service.serviceId ?? service.service?.id,
        productId: service.product?.id,
        ...(qty >= 1 ? { quantity: qty } : {}),
        price: parsed,
      },
    ];
    const res = await updateQuoteRequest({
      data: { id: rfq.id },
      update: { services, selectServices: selectedIds },
    });
    setSaving(false);
    if (res?.id) {
      toast.add("Price adjusted.", "var(--theme-ui-colors-success)");
      setEditingPrice(false);
      onChanged();
    } else {
      toast.add(res?.message || "Could not adjust price.", "var(--theme-ui-colors-danger)");
    }
  };

  // Live NTE delta against the estimate total for this service.
  const lineTotal =
    (service.quantity || 0) * parseFloat(service.price || 0) +
    (service.addons || []).reduce(
      (sum: number, a: any) => sum + (a.quantity > 0 ? a.quantity * parseFloat(a.price || 0) : 0),
      0,
    );
  const over = nteAmount != null ? lineTotal - nteAmount : null;

  return (
    <Box sx={{ padding: "0 20px 20px" }}>
      <Flex sx={{ alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        {/* Provider swap */}
        <Box
          as="button"
          onClick={loadAlternatives}
          sx={{
            background: mnt.cardBg,
            border: `1px solid ${themevals.colors.inputOutline}`,
            borderRadius: "4px",
            padding: "6px 12px",
            fontFamily: "Poppins",
            fontSize: "12px",
            color: themevals.colors.primary,
            cursor: "pointer",
          }}
        >
          {open ? "Hide providers" : "Change provider"}
        </Box>

        {/* Price override */}
        {!editingPrice ? (
          <Box
            as="button"
            onClick={() => {
              setPriceInput(String(service?.price ?? ""));
              setEditingPrice(true);
            }}
            sx={{
              background: mnt.cardBg,
              border: `1px solid ${themevals.colors.inputOutline}`,
              borderRadius: "4px",
              padding: "6px 12px",
              fontFamily: "Poppins",
              fontSize: "12px",
              color: themevals.colors.primary,
              cursor: "pointer",
            }}
          >
            Adjust price
          </Box>
        ) : (
          <Flex sx={{ alignItems: "center", gap: "6px" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey }}>$</Box>
            <input
              type="number"
              min={0}
              step="0.01"
              value={priceInput}
              onChange={(e: any) => setPriceInput(e.target.value)}
              style={{
                width: "90px",
                padding: "5px 8px",
                fontFamily: "Poppins",
                fontSize: "12px",
                border: `1px solid ${themevals.colors.inputOutline}`,
                borderRadius: "4px",
              }}
            />
            <Box
              as="button"
              onClick={saveOverride}
              sx={{
                background: mnt.ink,
                border: "none",
                borderRadius: "4px",
                padding: "6px 12px",
                fontFamily: "Poppins",
                fontSize: "12px",
                color: "#fff",
                cursor: "pointer",
                opacity: saving ? 0.6 : 1,
              }}
            >
              Save
            </Box>
            <Box
              as="button"
              onClick={() => setEditingPrice(false)}
              sx={{
                background: "transparent",
                border: "none",
                fontFamily: "Poppins",
                fontSize: "12px",
                color: themevals.colors.dkgrey,
                cursor: "pointer",
              }}
            >
              Cancel
            </Box>
          </Flex>
        )}

        {/* Live NTE delta */}
        {over != null && (
          <Box
            sx={{
              marginLeft: "auto",
              padding: "4px 12px",
              borderRadius: "12px",
              fontFamily: "Poppins",
              fontSize: "11.5px",
              fontWeight: "600",
              background: over > 0 ? mnt.amberPill : mnt.tealPill,
              color: over > 0 ? mnt.amber : mnt.teal,
            }}
          >
            {over > 0
              ? `Over NTE by ${money(over)}`
              : over === 0
              ? "At NTE"
              : `Under NTE by ${money(Math.abs(over))}`}
          </Box>
        )}
      </Flex>

      {/* Alternatives list */}
      {open && (
        <Box
          sx={{
            marginTop: "12px",
            border: `1px solid ${themevals.colors.inputOutline}`,
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          {loadingAlts ? (
            <Box sx={{ padding: "16px", textAlign: "center" }}>
              <Spinner size={18} strokeWidth={3} title="loading" />
            </Box>
          ) : alternatives.length === 0 ? (
            <Box sx={{ padding: "14px", fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey }}>
              No provider coverage is priced for this site and product.
            </Box>
          ) : (
            // Part 2 (SESSION_56_BRIEF) — the endpoint returns every
            // covering depot INCLUDING the current one, so a site that
            // only one depot covers comes back as a single row (the
            // current), which read as an empty, pointless panel. Say so
            // plainly instead. Investigated against production: coverage
            // for the mainstream Playground Mulch product is rich (dozens
            // of covering depots per region on a consistent service id),
            // so the filter is not dropping depots — a lone row is thin
            // real coverage at that site, not an endpoint bug.
            <>
              {alternatives.filter((a: any) => !a.isCurrent).length === 0 && (
                <Box
                  sx={{
                    padding: "12px 14px",
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    color: themevals.colors.dkgrey,
                    borderBottom: `1px solid ${themevals.colors.inputOutline}`,
                  }}
                >
                  No other provider covers this site for this service. The provider below is the
                  only option here; adjust the price above if you need a different number.
                </Box>
              )}
              {alternatives.map((alt: any, i: number) => (
              <Flex
                key={alt.depotId + "-" + i}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "10px 14px",
                  borderBottom: i < alternatives.length - 1 ? `1px solid ${themevals.colors.inputOutline}` : "none",
                  background: alt.isCurrent ? mnt.bgBeige : mnt.cardBg,
                }}
              >
                <Box>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: "600", color: "#000" }}>
                    {alt.providerName}
                    {alt.isCurrent && (
                      <Span color={themevals.colors.dkgrey} fontWeight="400" fontSize="11px"> · current</Span>
                    )}
                  </Box>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey }}>
                    {alt.depotName} · {money(alt.pricePerUnit)}/unit
                  </Box>
                </Box>
                {!alt.isCurrent && (
                  <Box
                    as="button"
                    onClick={() => swapProvider(alt)}
                    sx={{
                      background: mnt.cardBg,
                      border: `1px solid ${themevals.colors.inputOutline}`,
                      borderRadius: "4px",
                      padding: "5px 12px",
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      color: themevals.colors.primary,
                      cursor: "pointer",
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    Use this
                  </Box>
                )}
              </Flex>
              ))}
            </>
          )}

          {/* Depots that reach this site and carry the product but aren't
              set up for this service. Not selectable — a provisioning
              signal so staff can see WHY coverage is thin and which depot
              to onboard, instead of the panel silently hiding them. */}
          {unprovisioned.length > 0 && (
            <Box
              sx={{
                padding: "10px 14px",
                borderTop: `1px solid ${themevals.colors.inputOutline}`,
                background: mnt.bgBeige,
              }}
            >
              <Box
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: themevals.colors.dkgrey,
                  marginBottom: "4px",
                }}
              >
                Nearby, not set up for this service
              </Box>
              {unprovisioned.map((u: any, i: number) => (
                <Box
                  key={u.depotId + "-" + i}
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11px",
                    color: themevals.colors.dkgrey,
                    padding: "2px 0",
                  }}
                >
                  {u.providerName}
                  {u.depotName ? ` · ${u.depotName}` : ""}
                </Box>
              ))}
              <Box
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "10px",
                  color: themevals.colors.dkgrey,
                  marginTop: "4px",
                  fontStyle: "italic",
                }}
              >
                These depots cover the site but do not offer this service
                yet. Add the service to a depot catalog to make it
                selectable here.
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
